/** Shared color-math constants for the image-editor.
 *
 *  Centralized so the CPU adjustment math and the GPU shader code
 *  reference the same values — drift between them would silently
 *  break byte parity between the GPU and CPU adjustment paths. */

/** Rec. 709 / sRGB luma weights. Industry-standard for the color
 *  space the editor renders in; used by Color Balance, Shadows /
 *  Highlights, and the shared GLSL helpers when weighting tonal
 *  bands by luminance.
 *
 *  Sum to 1.0 by construction. If a future feature needs a
 *  different color space (e.g. Rec. 2020, Display P3), add a new
 *  named constant set rather than mutating these — every site
 *  that imports `LUMA_*` assumes Rec. 709 today. */
export const LUMA_R = 0.2126;
export const LUMA_G = 0.7152;
export const LUMA_B = 0.0722;

/** BT.601 / NTSC luma weights. Older standard than Rec. 709 but
 *  still in active use for several "perceptual-brightness" filters
 *  in this editor — Find Edges and Threshold both use it, matching
 *  the convention many legacy image-processing libraries
 *  established. The visual difference vs Rec. 709 is small on
 *  most natural-photo content; preserving the BT.601 weights
 *  keeps existing user output stable.
 *
 *  Centralized here (rather than inlined per-filter) so the JS
 *  CPU paths and the GLSL shaders share a single source of truth
 *  and can't drift. */
export const BT601_LUMA_R = 0.299;
export const BT601_LUMA_G = 0.587;
export const BT601_LUMA_B = 0.114;

/** BT.601 / NTSC chroma weights. The RGB → YCbCr matrix continues
 *  on from the BT.601 luma row: these are the Cb (blue-difference)
 *  and Cr (red-difference) projections. Centralized so the CPU
 *  Smart Sharpen filter and the GPU shader share the same values
 *  — drift between them silently breaks byte parity through the
 *  YCbCr round-trip. */
export const BT601_CB_R = -0.168736;
export const BT601_CB_G = -0.331264;
export const BT601_CB_B = 0.5;
export const BT601_CR_R = 0.5;
export const BT601_CR_G = -0.418688;
export const BT601_CR_B = -0.081312;

/** BT.601 inverse-transform weights for YCbCr → RGB. Same parity
 *  rationale as the forward weights — these get interpolated into
 *  the GPU YCbCr GLSL helper, so the CPU and GPU paths must read
 *  the same source. */
export const BT601_CR_TO_R = 1.402;
export const BT601_CB_TO_G = -0.344136;
export const BT601_CR_TO_G = -0.714136;
export const BT601_CB_TO_B = 1.772;
